// src/screens/SEO.js
// Google Search Console performance dashboard.
//
// Data source: scheduled Supabase function pulls Search Console data nightly
// via the GSC API → public.seo_queries (and seo_summary). RPC \`admin_get_seo_summary\`
// returns the rolled-up week-over-week numbers; \`admin_get_seo_top_queries\` returns
// the top 50 queries by clicks for the last 28 days.
//
// Until the data pipeline is wired, the screen shows a clear empty state explaining
// how to connect Search Console.

import React, { useState, useEffect } from 'react';
import { MetricCard, Card, CardTitle, Grid, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const fmt = (n) => (n == null ? '—' : n.toLocaleString());
const pct = (n) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);

function Skel({ h = 12, mb = 10 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

function ConnectGSC() {
  return (
    <Card>
      <CardTitle>Connect Google Search Console</CardTitle>
      <div style={{ fontFamily: F, fontSize: 13, color: '#4e7f97', lineHeight: 1.6, padding: '8px 0 16px' }}>
        <p style={{ marginTop: 0 }}>
          No Search Console data found yet. To wire this up:
        </p>
        <ol style={{ paddingLeft: 20, margin: '8px 0' }}>
          <li>Verify sanoire.com in <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer">Google Search Console</a></li>
          <li>Submit a sitemap (Vercel auto-generates one at <code>/sitemap.xml</code>)</li>
          <li>Create a Supabase scheduled function that calls the GSC API nightly and writes to <code>public.seo_queries</code> + <code>public.seo_summary</code> tables</li>
          <li>Implement RPCs: <code>admin_get_seo_summary()</code> and <code>admin_get_seo_top_queries(limit)</code></li>
        </ol>
        <p style={{ marginBottom: 0 }}>Once the pipeline is live, this screen surfaces clicks, impressions, average position, and CTR by query.</p>
      </div>
    </Card>
  );
}

export default function SEO() {
  const [summary, setSummary] = useState(null);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [setupNeeded, setSetupNeeded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: s, error: e1 } = await supabase.rpc('admin_get_seo_summary');
        const { data: q, error: e2 } = await supabase.rpc('admin_get_seo_top_queries', { limit: 50 });
        if (cancelled) return;
        if (e1 || e2 || !s) {
          setSetupNeeded(true);
        } else {
          setSummary(s);
          setQueries(q || []);
        }
      } catch (_e) {
        if (!cancelled) setSetupNeeded(true);
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ fontFamily: F, padding: 20 }}>
        <Skel h={32} mb={20} />
        <Skel h={120} mb={16} />
        <Skel h={400} />
      </div>
    );
  }

  if (setupNeeded) {
    return (
      <div style={{ fontFamily: F, padding: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 500, color: '#163040', margin: '0 0 4px' }}>SEO</h1>
        <p style={{ color: '#6F8FA7', fontSize: 13, margin: '0 0 24px' }}>Google Search Console performance — clicks, impressions, position, CTR</p>
        <ConnectGSC />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: F, padding: 20 }}>
      <h1 style={{ fontSize: 24, fontWeight: 500, color: '#163040', margin: '0 0 4px' }}>SEO</h1>
      <p style={{ color: '#6F8FA7', fontSize: 13, margin: '0 0 24px' }}>Last 28 days · Google Search Console</p>

      <Grid>
        <MetricCard label="Clicks" value={fmt(summary?.clicks)} delta={summary?.clicks_delta} />
        <MetricCard label="Impressions" value={fmt(summary?.impressions)} delta={summary?.impressions_delta} />
        <MetricCard label="Avg position" value={summary?.position?.toFixed(1) || '—'} delta={summary?.position_delta ? -summary.position_delta : null} />
        <MetricCard label="CTR" value={pct(summary?.ctr)} delta={summary?.ctr_delta} />
      </Grid>

      <Card style={{ marginTop: 20 }}>
        <CardTitle>Top 50 queries · last 28 days</CardTitle>
        <table style={{ width: '100%', fontSize: 12, marginTop: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#6F8FA7', borderBottom: '.5px solid #d0dde7' }}>
              <th style={{ padding: '6px 8px' }}>Query</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Clicks</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Impressions</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Position</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>CTR</th>
            </tr>
          </thead>
          <tbody>
            {queries.map((q, i) => (
              <tr key={i} style={{ borderBottom: '.5px solid #edf2f5' }}>
                <td style={{ padding: '8px', color: '#163040' }}>{q.query}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(q.clicks)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(q.impressions)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{q.position?.toFixed(1) || '—'}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{pct(q.ctr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Insight title="Underperforming query opportunities">
        Watch for queries climbing in impressions but not yet clicks — those are positions 11-20 where a CTR improvement
        (better title/description) can unlock 10x traffic. Prioritise these over chasing new keywords.
      </Insight>
    </div>
  );
}
